import { useEffect, useRef, useState } from 'react';
import { DOCUMENT_KINDS, DocumentKind, type DocumentPrompt } from '@jabberr/core';
import { api, errorMessage } from '../lib/api.js';

type Send = (content: string, clientTurnId?: string, purpose?: DocumentKind) => Promise<void>;

export const PARK_DESCRIPTION = 'Save it as a note with a short write-up. Nothing gets built.';

/** The document kinds in their fixed order, for menus that need labels before the prompts have loaded. */
export const WRITE_KINDS: { kind: DocumentKind; label: string; description: string }[] = DocumentKind.options.map((kind) => ({
  kind,
  ...DOCUMENT_KINDS[kind],
}));

/** Sends the fixed document turn for one kind. The server substitutes its own copy of the prompt for a turn with a
 *  purpose, so the words are always the same; the prompt list is fetched once and only to send its content. Works in
 *  any active conversation: chat, transcript review or meeting. */
export async function sendDocumentTurn(kind: DocumentKind, send: Send, cache: { docs: DocumentPrompt[] | null }): Promise<void> {
  if (!cache.docs) cache.docs = (await api.outputs.documents()).documents;
  const doc = cache.docs.find((d) => d.kind === kind);
  if (!doc) throw new Error(`${DOCUMENT_KINDS[kind].label} is not available right now`);
  await send(doc.content, crypto.randomUUID(), kind);
}

/** "Write..." : sends one of the fixed document turns (prompts/<kind>-turn.md) through the normal pipeline. */
export function WriteMenu({
  disabled,
  send,
  onError,
  onPark,
  documents = true,
}: {
  disabled: boolean;
  send: Send;
  onError: (message: string) => void;
  /** Add to parking lot (OPEN-38): Jabberr drafts a note from this conversation. Absent: not offered here. */
  onPark?: () => void;
  /** false: no document kinds (a guest cannot draft documents); the menu then holds only Add to parking lot. */
  documents?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState<DocumentPrompt[] | null>(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || docs || !documents) return;
    api.outputs
      .documents()
      .then((r) => setDocs(r.documents))
      .catch((err) => onError(errorMessage(err)));
  }, [open, docs, onError]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  const write = async (doc: DocumentPrompt): Promise<void> => {
    setOpen(false);
    setBusy(true);
    try {
      await send(doc.content, crypto.randomUUID(), doc.kind);
    } catch (err) {
      onError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="write-menu" ref={ref}>
      <button type="button" className="btn" onClick={() => setOpen((o) => !o)} disabled={busy || disabled} aria-haspopup="menu" aria-expanded={open} title="Ask for a document written from this conversation. It appears here and on the project's Outputs page.">
        Write...
      </button>
      {open ? (
        <div className="menu" role="menu">
          {onPark ? (
            <button
              type="button"
              className="menu-item"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onPark();
              }}
            >
              <span className="menu-title">Add to parking lot</span>
              <span className="muted tiny">{PARK_DESCRIPTION}</span>
            </button>
          ) : null}
          {!documents ? null : docs === null ? (
            <div className="muted small menu-item">Loading</div>
          ) : (
            docs.map((doc) => (
              <button key={doc.kind} type="button" className="menu-item" role="menuitem" onClick={() => void write(doc)}>
                <span className="menu-title">{doc.label}</span>
                <span className="muted tiny">{doc.description}</span>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
